import {
  BooleanFilter,
  SelectFilter,
  usePermissions,
  WbCard,
  WbCardContent,
  WbTable,
  WbTableFilters,
  SortingState,
} from '@agilelab/plugin-wb-platform';
import { FC, useCallback, useEffect, useState } from 'react';
import { ScaffolderTask } from '../../types';
import {
  CreatedAtColumn,
  OwnerEntityColumn,
  TaskStatusColumn,
  TemplateTitleColumn,
} from './columns';
import { useNavigate } from 'react-router';
import { SubRouteRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import { Box } from '@material-ui/core';
import { TemplateTypeColumn } from './columns/TemplateTypeColumn';
import { UserEntity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { platformSettingsEditPermission } from '@agilelab/plugin-wb-rbac-common';
import { scaffolderApiRef } from '../../api';
import { getTemplateTypeDisplayName } from '../../template.utils';

interface TasksFilters {
  search: string;
  type: string;
  owned?: boolean;
}

interface Props {
  tasks: ScaffolderTask[];
  ownerFilter: 'owned' | 'all';
  setOwnerFilter: (value: 'owned' | 'all') => void;
  elementRouteRef: SubRouteRef;
  filters: TasksFilters;
  onSearchChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onOwnedChange: (value: boolean | undefined) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  totalTasks: number;
  isServerPagination: boolean;
  isFetching: boolean;
  sorting?: SortingState;
  onSortingChange?: React.Dispatch<React.SetStateAction<SortingState>>;
}

export const ListTasksTable: FC<Props> = ({
  tasks,
  ownerFilter,
  setOwnerFilter,
  elementRouteRef,
  filters,
  onSearchChange,
  onTypeChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  totalTasks,
  isServerPagination,
  isFetching,
  sorting,
  onSortingChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [availableTypes, setAvailableTypes] = useState<Array<string>>([]);
  const [taskEntity, setTaskWithEntity] = useState<ScaffolderTask[]>([]);
  const [data, setData] = useState<ScaffolderTask[]>(tasks);
  const navigate = useNavigate();
  const elementLink = useRouteRef(elementRouteRef);
  const catalogApi = useApi(catalogApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const res = await Promise.all(
      tasks.map(async element => {
        if (element.spec.templateInfo && element.spec.templateInfo?.entityRef) {
          const template = await catalogApi.getEntityByRef(
            element.spec.templateInfo?.entityRef,
          );

          element.spec.templateInfo = {
            ...element.spec.templateInfo,
            entity: template ? template : element.spec.templateInfo.entity,
          };
        }
        return element;
      }),
    );
    setTaskWithEntity(res);
    setLoading(false);
  }, [catalogApi, tasks]);

  useEffect(() => {
    fetchData();
  }, [fetchData, tasks]);

  useEffect(() => {
    let isMounted = true;

    const listTaskTypes = scaffolderApi.listTaskTypes;
    if (typeof listTaskTypes !== 'function') {
      return () => {
        isMounted = false;
      };
    }

    const loadTypes = async () => {
      try {
        const response = await listTaskTypes.call(scaffolderApi);
        if (!isMounted) {
          return;
        }
        setAvailableTypes(response.types ?? []);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch scaffolder task types', error);
      }
    };

    loadTypes();

    return () => {
      isMounted = false;
    };
  }, [scaffolderApi]);

  useEffect(() => {
    setData(taskEntity);
  }, [taskEntity]);

  const { allAllowed: allowedEditorActions } = usePermissions([
    {
      permission: platformSettingsEditPermission,
    },
  ]);

  return (
    <WbCard title="Tasks List">
      <WbCardContent>
        <Box marginBottom="16px">
          <WbTableFilters
            searchValue={filters.search}
            onSearch={onSearchChange}
          >
            <SelectFilter
              field="Type"
              value={filters.type}
              onChange={v => {
                onTypeChange(v || '');
              }}
              options={availableTypes}
              renderOption={o => getTemplateTypeDisplayName(o)}
              renderValue={v => getTemplateTypeDisplayName(v)}
            />

            {allowedEditorActions && (
              <BooleanFilter
                field="Owned"
                value={ownerFilter === 'all' ? false : true}
                onChange={v => {
                  setOwnerFilter(v ? 'owned' : 'all');
                }}
              />
            )}
          </WbTableFilters>
        </Box>

        <WbTable<ScaffolderTask>
          components={{
            tableLoader: {
              loading: loading || isFetching,
            },
            tableContent: {
              onRowClick: (row, event) => {
                const tag = (event.target as HTMLElement).tagName;
                const isLink = tag === 'A';
                event.preventDefault();
                event.stopPropagation();
                // if (!isLink) navigate(`${rootLink()}/tasks/${row.id}`);
                if (!isLink) navigate(`${elementLink({ taskId: row.id })}`);
              },
              rows: isServerPagination
                ? data
                : data.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage,
                  ) || [],
              columns: [
                {
                  headerName: 'Task ID',
                  field: 'id',
                  sortable: isServerPagination,
                },
                {
                  headerName: 'Template Type',
                  field: 'spec',
                  uniqueFieldId: 'templateType',
                  fieldRender: row => (
                    <TemplateTypeColumn template={row.spec.templateInfo} />
                  ),
                  sortable: false,
                },
                {
                  headerName: 'Template Name',
                  field: 'spec',
                  uniqueFieldId: 'templateName',
                  fieldRender: row => (
                    <TemplateTitleColumn template={row.spec.templateInfo} />
                  ),
                  sortable: false,
                },
                {
                  headerName: 'Created',
                  field: 'createdAt',
                  fieldRender: row => (
                    <CreatedAtColumn createdAt={row.createdAt} />
                  ),
                  sortable: isServerPagination,
                },
                {
                  headerName: 'Owner',
                  field: 'spec',
                  uniqueFieldId: 'owner',
                  fieldRender: row => {
                    const userEntity = row?.spec?.user?.entity as UserEntity;

                    return (
                      <OwnerEntityColumn
                        entityRef={row.spec?.user?.ref}
                        userEntity={userEntity}
                      />
                    );
                  },
                  sortable: false,
                },
                {
                  headerName: 'Status',
                  field: 'status',
                  fieldRender: row => <TaskStatusColumn status={row.status} />,
                  sortable: isServerPagination,
                },
              ],
            },
          }}
          pagination={{
            rowsPerPageOptions: [5, 10, 25],
            count: isServerPagination ? totalTasks : data.length,
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            onPageChange,
            onRowsPerPageChange,
          }}
          sorting={sorting}
          onSortingChange={onSortingChange}
          manualSorting={isServerPagination}
        />
      </WbCardContent>
    </WbCard>
  );
};
